import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  Fade,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useAuth } from "../../context/AuthContext";
import { getCurrentMode } from "../../utils/api";
// Import logo from public folder
const headerLogo = "/assets/images/teamscout-logo.png";
const easterEggGif = "/assets/images/easterEgg.gif";

const Header = ({ open, toggleDrawer }) => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [logoClickCount, setLogoClickCount] = React.useState(0);
  const [showEasterEgg, setShowEasterEgg] = React.useState(false);
  const [clickTimeout, setClickTimeout] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleLogoClick = (event) => {
    event.preventDefault();

    // Clear existing timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
    }

    const newClickCount = logoClickCount + 1;
    setLogoClickCount(newClickCount);

    if (newClickCount === 5) {
      setShowEasterEgg(true);
      setLogoClickCount(0);
    } else {
      // Reset click count after 2 seconds of no clicks
      const timeout = setTimeout(() => {
        setLogoClickCount(0);
      }, 2000);
      setClickTimeout(timeout);
    }
  };

  const handleCloseEasterEgg = () => {
    setShowEasterEgg(false);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "white",
        color: "black",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <Toolbar>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={toggleDrawer}
          sx={{
            mr: { xs: 1, sm: 2 },
            color: "black",
            flexShrink: 0,
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box
          sx={{
            flexGrow: 1,
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            alignItems: "center",
            minWidth: 0, // Allow shrinking
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            onClick={handleLogoClick}
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              transition: "transform 0.1s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            <img
              src={headerLogo}
              alt="Team Scout Logo"
              style={{
                height: isMobile ? 32 : 40,
                marginRight: isMobile ? 8 : 12,
                maxWidth: "100%",
              }}
            />
          </Box>

          {/* Easter Egg GIF - appears next to logo */}
          <Fade in={showEasterEgg} timeout={500}>
            <Box
              onClick={handleCloseEasterEgg}
              sx={{
                cursor: "pointer",
                marginLeft: 2,
                display: "flex",
                alignItems: "center",
                animation: showEasterEgg ? "bounce 0.6s ease-out" : "none",
                "@keyframes bounce": {
                  "0%": {
                    transform: "scale(0.3) rotate(-10deg)",
                    opacity: 0,
                  },
                  "50%": {
                    transform: "scale(1.1) rotate(5deg)",
                  },
                  "70%": {
                    transform: "scale(0.9) rotate(-2deg)",
                  },
                  "100%": {
                    transform: "scale(1) rotate(0deg)",
                    opacity: 1,
                  },
                },
              }}
            >
              <img
                src={easterEggGif}
                alt="Easter Egg!"
                style={{
                  height: "auto",
                  maxHeight: isMobile ? "40px" : "60px",
                  width: "auto",
                }}
              />
            </Box>
          </Fade>
        </Box>

        {/* Mode indicator - chip on desktop, dot on mobile */}
        {isMobile ? (
          <Tooltip title={`Mode: ${getCurrentMode()}`}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor:
                  getCurrentMode() === "development"
                    ? "success.main"
                    : "grey.400",
                mr: 1,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        ) : (
          <Chip
            label={`Mode: ${getCurrentMode()}`}
            color={getCurrentMode() === "development" ? "success" : "default"}
            size="small"
            sx={{ mr: 2, flexShrink: 0 }}
          />
        )}

        {user ? (
          <Box sx={{ flexShrink: 0 }}>
            <Tooltip title={`${user.firstName} ${user.lastName}`}>
              <IconButton
                size={isMobile ? "small" : "medium"}
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{
                  ml: { xs: 0.5, sm: 1 },
                  border: "2px solid",
                  borderColor: "primary.main",
                  padding: isMobile ? 0.3 : 0.8,
                  minWidth: "auto",
                  flexShrink: 0,
                }}
              >
                {user.avatar ? (
                  <Avatar
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                    sx={{
                      width: isMobile ? 24 : 32,
                      height: isMobile ? 24 : 32,
                    }}
                  />
                ) : (
                  <AccountCircleIcon
                    sx={{
                      width: isMobile ? 24 : 32,
                      height: isMobile ? 24 : 32,
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                component={RouterLink}
                to="/profile"
                onClick={handleClose}
              >
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to="/login"
            size={isMobile ? "small" : "medium"}
            sx={{
              flexShrink: 0,
              minWidth: { xs: "auto", sm: "64px" },
              px: { xs: 1.5, sm: 2 },
            }}
          >
            {isMobile ? "Login" : "Login"}
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
